import React, { useState, useEffect, useRef } from 'react';
import './PaySlip.css';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import logo from "../assets/logo.png";

/* ---------- Slip design constants ---------- */
const COMPANY_NAME = 'QNEXA SERVICES';
const COMPANY_TAGLINE = 'Engineering & Inspection Specialists';
const FONT = "'Calibri', 'Segoe UI', 'Helvetica Neue', Arial, sans-serif";
const NAVY = '#1f3a5f';
const BORDER = '1px solid #c9d1dc';

const cell = {
  border: BORDER,
  padding: '7px 10px',
  fontSize: '13px',
  verticalAlign: 'middle',
  wordBreak: 'break-word',
};
const labelCell = { ...cell, background: '#f3f5f8', fontWeight: 600, color: NAVY, width: '18%' };
const valueCell = { ...cell, width: '32%' };
const headCell = { ...cell, background: NAVY, color: '#fff', fontWeight: 600, textAlign: 'left' };
const amountCell = { ...cell, textAlign: 'right' };
const totalCell = { ...cell, background: '#e9eef5', fontWeight: 700, color: NAVY };

const fmt = (v) =>
  (parseFloat(v) || 0).toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const initialPaySlipData = {
  employeeName: '',
  employeeId: '',
  designation: '',
  department: '',
  bankAccount: '',
  panNumber: '',
  payPeriod: '',
  daysWorked: '',

  // Earnings
  basicSalary: '',
  hra: '',
  conveyance: '',
  medicalAllowance: '',
  specialAllowance: '',
  overtime: '',
  bonus: '',

  // Deductions
  providentFund: '',
  professionalTax: '',
  tds: '',
  loanDeduction: '',
  otherDeductions: '',
};

const PaySlip = () => {
  useEffect(() => {
    document.title = "Pay-Slips | Qnexa Services — Engineering & Inspection Specialists";
  }, []);

  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [credentials, setCredentials] = useState({ email: '', password: '' });
  const [error, setError] = useState('');
  const [paySlipData, setPaySlipData] = useState(initialPaySlipData);
  const [generatedSlip, setGeneratedSlip] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);

  const slipRef = useRef(null);

  const handleLogin = (e) => {
    e.preventDefault();
    setError('');

    if (credentials.email === 'qnexservices@gmail.com' && credentials.password === 'Qnexa@123') {
      setIsLoggedIn(true);
      localStorage.setItem('isLoggedIn', 'true');
    } else {
      setError('Invalid email or password. Please try again.');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setPaySlipData((prev) => ({ ...prev, [name]: value }));
  };

  const calculateTotalEarnings = () => {
    const earnings = [
      'basicSalary', 'hra', 'conveyance', 'medicalAllowance',
      'specialAllowance', 'overtime', 'bonus',
    ];
    return earnings.reduce((total, field) => total + (parseFloat(paySlipData[field]) || 0), 0);
  };

  const calculateTotalDeductions = () => {
    const deductions = ['providentFund', 'professionalTax', 'tds', 'loanDeduction', 'otherDeductions'];
    return deductions.reduce((total, field) => total + (parseFloat(paySlipData[field]) || 0), 0);
  };

  const calculateNetSalary = () => calculateTotalEarnings() - calculateTotalDeductions();

  const generatePaySlip = (e) => {
    e.preventDefault();
    setIsGenerating(true);

    if (!paySlipData.employeeName || !paySlipData.employeeId || !paySlipData.payPeriod || !paySlipData.basicSalary) {
      alert('Please fill all required fields (marked with *)');
      setIsGenerating(false);
      return;
    }

    setTimeout(() => {
      const slip = {
        ...paySlipData,
        totalEarnings: calculateTotalEarnings(),
        totalDeductions: calculateTotalDeductions(),
        netSalary: calculateNetSalary(),
        slipNumber: `PS-${Date.now().toString().slice(-6)}`,
        generatedDate: new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }),
      };

      setGeneratedSlip(slip);
      setIsGenerating(false);
    }, 1500);
  };

  /* ---------- Capture helper (shared by PDF + Image) ---------- */
  const captureSlip = () =>
    html2canvas(slipRef.current, {
      scale: 2,
      useCORS: true,
      backgroundColor: '#ffffff',
      logging: false,
      scrollX: 0,
      scrollY: -window.scrollY,
      onclone: (clonedDoc) => {
        // Make sure no parent clips the slip while capturing
        let el = clonedDoc.getElementById('pay-slip-content');
        while (el) {
          el.style.overflow = 'visible';
          el = el.parentElement;
        }
      },
    });

  const fileBaseName = () =>
    `PaySlip_${generatedSlip.employeeId}_${generatedSlip.payPeriod.replace(/\s+/g, '_')}`;

  /* ---------- PDF: always ONE A4 page ---------- */
  const downloadAsPDF = () => {
    if (!slipRef.current || !generatedSlip) return;

    captureSlip()
      .then((canvas) => {
        const imgData = canvas.toDataURL('image/png');
        const pdf = new jsPDF('p', 'mm', 'a4');

        const pageW = pdf.internal.pageSize.getWidth();   // 210
        const pageH = pdf.internal.pageSize.getHeight();  // 297
        const margin = 8;
        const maxW = pageW - margin * 2;
        const maxH = pageH - margin * 2;

        // Fit inside the page both ways, so it can never go to page 2
        const ratio = canvas.width / canvas.height;
        let w = maxW;
        let h = w / ratio;
        if (h > maxH) {
          h = maxH;
          w = h * ratio;
        }
        const x = (pageW - w) / 2;
        const y = margin;

        pdf.addImage(imgData, 'PNG', x, y, w, h);
        pdf.save(`${fileBaseName()}.pdf`);
      })
      .catch((err) => {
        console.error('Error generating PDF:', err);
        alert('Error generating PDF. Please try again.');
      });
  };

  const downloadAsImage = () => {
    if (!slipRef.current || !generatedSlip) return;

    captureSlip()
      .then((canvas) => {
        const link = document.createElement('a');
        link.download = `${fileBaseName()}.png`;
        link.href = canvas.toDataURL('image/png');
        link.click();
      })
      .catch((err) => {
        console.error('Error generating image:', err);
        alert('Error generating image. Please try again.');
      });
  };

  /* ---------- Print: one A4 page ---------- */
  const printPaySlip = () => {
    if (!slipRef.current) return;

    const printContent = slipRef.current.outerHTML;
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Pay Slip - ${generatedSlip.employeeName}</title>
        <style>
          @page { size: A4; margin: 8mm; }
          html, body { margin: 0; padding: 0; font-family: ${FONT};
            -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .slip-doc { width: 100% !important; border: none !important; box-shadow: none !important;
            page-break-inside: avoid; }
        </style>
      </head>
      <body>
        ${printContent}
        <script>
          window.onload = function() {
            window.print();
            setTimeout(function() { window.close(); }, 1000);
          }
        </script>
      </body>
      </html>
    `);
    printWindow.document.close();
  };

  /* ---------- Number to words (Indian system) ---------- */
  const numberToWords = (num) => {
    if (num === 0 || isNaN(num)) return 'Zero Rupees Only';

    const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine',
      'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
    const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

    const convertBelowHundred = (n) => {
      if (n === 0) return '';
      if (n < 20) return ones[n];
      const ten = Math.floor(n / 10);
      const one = n % 10;
      return tens[ten] + (one > 0 ? ' ' + ones[one] : '');
    };

    const convertBelowThousand = (n) => {
      if (n === 0) return '';
      const hundred = Math.floor(n / 100);
      const remainder = n % 100;
      let result = '';
      if (hundred > 0) {
        result += ones[hundred] + ' Hundred';
        if (remainder > 0) result += ' ';
      }
      if (remainder > 0) result += convertBelowHundred(remainder);
      return result;
    };

    let result = '';
    let number = Math.floor(num);

    const crores = Math.floor(number / 10000000);
    if (crores > 0) {
      result += convertBelowHundred(crores) + ' Crore ';
      number %= 10000000;
    }

    const lakhs = Math.floor(number / 100000);
    if (lakhs > 0) {
      result += convertBelowHundred(lakhs) + ' Lakh ';
      number %= 100000;
    }

    const thousands = Math.floor(number / 1000);
    if (thousands > 0) {
      result += convertBelowHundred(thousands) + ' Thousand ';
      number %= 1000;
    }

    if (number > 0) result += convertBelowThousand(number);

    result = result.trim().replace(/\s+/g, ' ');
    if (result === '') result = 'Zero';
    return result + ' Rupees Only';
  };

  const handleLogout = () => {
    setIsLoggedIn(false);
    setGeneratedSlip(null);
    localStorage.removeItem('isLoggedIn');
    setCredentials({ email: '', password: '' });
  };

  useEffect(() => {
    const storedLogin = localStorage.getItem('isLoggedIn');
    if (storedLogin === 'true') setIsLoggedIn(true);
  }, []);

  /* ---------- Form field helpers ---------- */
  const field = ({ name, label, type = 'text', placeholder = '', required = false }) => (
    <div className="form-group">
      <label>
        {label} {required && <span className="required">*</span>}
      </label>
      <input
        type={type}
        name={name}
        value={paySlipData[name]}
        onChange={handleInputChange}
        placeholder={placeholder}
        required={required}
      />
    </div>
  );

  /* ---------- THE PAY SLIP DOCUMENT (preview + PDF + image + print) ---------- */
  const renderSlipDocument = () => {
    const s = generatedSlip;

    const earnings = [
      ['Basic Salary', s.basicSalary],
      ['House Rent Allowance (HRA)', s.hra],
      ['Conveyance Allowance', s.conveyance],
      ['Medical Allowance', s.medicalAllowance],
      ['Special Allowance', s.specialAllowance],
      ['Overtime', s.overtime],
      ['Bonus / Incentives', s.bonus],
    ].filter(([, v], i) => i === 0 || parseFloat(v) > 0);

    const deductions = [
      ['Provident Fund (PF)', s.providentFund],
      ['Professional Tax', s.professionalTax],
      ['Tax Deducted at Source (TDS)', s.tds],
      ['Loan Deduction', s.loanDeduction],
      ['Other Deductions', s.otherDeductions],
    ].filter(([, v]) => parseFloat(v) > 0);

    const rowCount = Math.max(earnings.length, deductions.length, 1);
    const rows = Array.from({ length: rowCount }, (_, i) => ({
      e: earnings[i],
      d: deductions[i],
    }));

    const employeeRows = [
      ['Employee Name', s.employeeName, 'Employee ID', s.employeeId],
      ['Designation', s.designation, 'Department', s.department],
      ['Bank A/C No.', s.bankAccount, 'PAN', s.panNumber],
      ['Pay Period', s.payPeriod, 'Days Worked', s.daysWorked],
      ['Slip No.', s.slipNumber, 'Date of Issue', s.generatedDate],
    ];

    return (
      <div
        ref={slipRef}
        id="pay-slip-content"
        className="slip-doc"
        style={{
          width: '794px',
          boxSizing: 'border-box',
          background: '#ffffff',
          padding: '32px 36px',
          fontFamily: FONT,
          color: '#1a1a1a',
          fontSize: '13px',
          border: BORDER,
          margin: '0 auto',
        }}
      >
        {/* Header */}
<div
  style={{
    display: 'grid',
    gridTemplateColumns: '1fr auto 1fr',
    alignItems: 'center',
    paddingBottom: '14px',
    borderBottom: `3px solid ${NAVY}`,
  }}
>
  {/* Left: logo */}
  <div style={{ display: 'flex', alignItems: 'center' }}>
    <img src={logo} alt="Qnexa Services" style={{ height: '58px' }} />
  </div>

  {/* Centre: company name */}
  <div style={{ textAlign: 'center' }}>
    <div
      style={{
        fontSize: '26px',
        fontWeight: 700,
        color: NAVY,
        letterSpacing: '1px',
        lineHeight: 1.1,
      }}
    >
      {COMPANY_NAME}
    </div>
    <div style={{ fontSize: '12px', color: '#5b6573', marginTop: '4px' }}>
      {COMPANY_TAGLINE}
    </div>
  </div>

  {/* Right: pay slip title */}
  <div style={{ textAlign: 'right' }}>
    <div
      style={{
        fontSize: '22px',
        fontWeight: 700,
        color: NAVY,
        letterSpacing: '2px',
      }}
    >
      PAY SLIP
    </div>
    <div style={{ fontSize: '12px', color: '#5b6573', marginTop: '4px' }}>
      For the period of {s.payPeriod}
    </div>
  </div>
</div>

        {/* Employee details */}
        <div style={{ margin: '18px 0 6px', fontWeight: 700, color: NAVY, fontSize: '14px' }}>
          EMPLOYEE DETAILS
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
          <tbody>
            {employeeRows.map(([l1, v1, l2, v2]) => (
              <tr key={l1}>
                <td style={labelCell}>{l1}</td>
                <td style={valueCell}>{v1 || '-'}</td>
                <td style={labelCell}>{l2}</td>
                <td style={valueCell}>{v2 || '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Earnings & deductions */}
        <div style={{ margin: '18px 0 6px', fontWeight: 700, color: NAVY, fontSize: '14px' }}>
          SALARY DETAILS
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
          <thead>
            <tr>
              <th style={{ ...headCell, width: '31%' }}>Earnings</th>
              <th style={{ ...headCell, width: '19%', textAlign: 'right' }}>Amount (₹)</th>
              <th style={{ ...headCell, width: '31%' }}>Deductions</th>
              <th style={{ ...headCell, width: '19%', textAlign: 'right' }}>Amount (₹)</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i}>
                <td style={cell}>{r.e ? r.e[0] : '\u00A0'}</td>
                <td style={amountCell}>{r.e ? fmt(r.e[1]) : '\u00A0'}</td>
                <td style={cell}>{r.d ? r.d[0] : '\u00A0'}</td>
                <td style={amountCell}>{r.d ? fmt(r.d[1]) : '\u00A0'}</td>
              </tr>
            ))}
            <tr>
              <td style={totalCell}>Total Earnings</td>
              <td style={{ ...totalCell, textAlign: 'right' }}>{fmt(s.totalEarnings)}</td>
              <td style={totalCell}>Total Deductions</td>
              <td style={{ ...totalCell, textAlign: 'right' }}>{fmt(s.totalDeductions)}</td>
            </tr>
          </tbody>
        </table>

        {/* Net pay */}
        <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '14px' }}>
          <tbody>
            <tr>
              <td
                style={{
                  ...cell,
                  background: NAVY,
                  color: '#fff',
                  fontWeight: 700,
                  fontSize: '15px',
                  padding: '10px 12px',
                }}
              >
                NET SALARY PAYABLE
              </td>
              <td
                style={{
                  ...cell,
                  background: NAVY,
                  color: '#fff',
                  fontWeight: 700,
                  fontSize: '17px',
                  textAlign: 'right',
                  padding: '10px 12px',
                }}
              >
                ₹ {fmt(s.netSalary)}
              </td>
            </tr>
            <tr>
              <td colSpan={2} style={{ ...cell, background: '#f3f5f8' }}>
                <strong style={{ color: NAVY }}>Amount in words: </strong>
                {numberToWords(s.netSalary)}
              </td>
            </tr>
          </tbody>
        </table>

        {/* Signatures */}
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '56px' }}>
          <div style={{ width: '200px', borderTop: '1px solid #555', paddingTop: '6px', textAlign: 'center', fontSize: '12px' }}>
            Employee Signature
          </div>
          <div style={{ width: '200px', borderTop: '1px solid #555', paddingTop: '6px', textAlign: 'center', fontSize: '12px' }}>
            Authorised Signatory
          </div>
        </div>

        {/* Footer */}
        <div
          style={{
            marginTop: '22px',
            paddingTop: '8px',
            borderTop: BORDER,
            textAlign: 'center',
            fontSize: '11px',
            color: '#7a8493',
          }}
        >
          This is a computer-generated pay slip. Please contact the HR / Accounts department for any queries.
        </div>
      </div>
    );
  };

  /* ---------- LOGIN SCREEN ---------- */
  if (!isLoggedIn) {
    return (
      <div className="pay-slip-login">
        <div className="login-container">
          <div className="login-header">
            <img src={logo} alt="Qnexa Services" className="login-logo" />
            <h2>Pay Slip Portal</h2>
            <p>Access your pay slips and generate new ones</p>
          </div>

          <form className="login-form" onSubmit={handleLogin}>
            <div className="form-group">
              <label htmlFor="email">Email Address</label>
              <input
                type="email"
                id="email"
                value={credentials.email}
                onChange={(e) => setCredentials({ ...credentials, email: e.target.value })}
                placeholder="Enter your email"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="password">Password</label>
              <input
                type="password"
                id="password"
                value={credentials.password}
                onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
                placeholder="Enter your password"
                required
              />
            </div>

            {error && <div className="error-message">{error}</div>}

            <button type="submit" className="login-btn">Login to Pay Slip Portal</button>
          </form>
        </div>
      </div>
    );
  }

  /* ---------- MAIN PAGE ---------- */
  return (
    <div className="pay-slip-page">
      <div className="pay-slip-container">
        <div className="page-header">
          <div className="header-left">
            <img src={logo} alt="Qnexa Services" className="header-logo" />
            <h1>Pay Slip Generator</h1>
          </div>
          <button onClick={handleLogout} className="logout-btn">Logout</button>
        </div>

        <div className="pay-slip-content-grid">
          <div className="pay-slip-form-section">
            <div className="form-header">
              <h2>Employee & Pay Details</h2>
              <p className="form-subtitle">Fill in the details to generate pay slip</p>
            </div>

            <form className="pay-slip-form" onSubmit={generatePaySlip}>
              <div className="form-section">
                <h3>Employee Information</h3>
                <div className="form-row">
                  {field({ name: 'employeeName', label: 'Employee Name', placeholder: 'Enter employee name', required: true })}
                  {field({ name: 'employeeId', label: 'Employee ID', placeholder: 'Enter employee ID', required: true })}
                </div>
                <div className="form-row">
                  {field({ name: 'designation', label: 'Designation', placeholder: 'Enter designation' })}
                  {field({ name: 'department', label: 'Department', placeholder: 'Enter department' })}
                </div>
                <div className="form-row">
                  {field({ name: 'bankAccount', label: 'Bank Account Number', placeholder: 'Enter bank account number' })}
                  {field({ name: 'panNumber', label: 'PAN Number', placeholder: 'Enter PAN number' })}
                </div>
                <div className="form-row">
                  {field({ name: 'payPeriod', label: 'Pay Period', placeholder: 'e.g., January 2024', required: true })}
                  {field({ name: 'daysWorked', label: 'Days Worked', type: 'number', placeholder: 'e.g., 22' })}
                </div>
              </div>

              <div className="form-section">
                <h3>Earnings (₹)</h3>
                <div className="form-row">
                  {field({ name: 'basicSalary', label: 'Basic Salary', type: 'number', placeholder: '0', required: true })}
                  {field({ name: 'hra', label: 'House Rent Allowance (HRA)', type: 'number', placeholder: '0' })}
                </div>
                <div className="form-row">
                  {field({ name: 'conveyance', label: 'Conveyance Allowance', type: 'number', placeholder: '0' })}
                  {field({ name: 'medicalAllowance', label: 'Medical Allowance', type: 'number', placeholder: '0' })}
                </div>
                <div className="form-row">
                  {field({ name: 'specialAllowance', label: 'Special Allowance', type: 'number', placeholder: '0' })}
                  {field({ name: 'overtime', label: 'Overtime', type: 'number', placeholder: '0' })}
                </div>
                <div className="form-row">
                  {field({ name: 'bonus', label: 'Bonus / Incentives', type: 'number', placeholder: '0' })}
                  <div className="form-group total-display">
                    <label>Total Earnings</label>
                    <div className="total-value">₹{calculateTotalEarnings().toLocaleString('en-IN')}</div>
                  </div>
                </div>
              </div>

              <div className="form-section">
                <h3>Deductions (₹)</h3>
                <div className="form-row">
                  {field({ name: 'providentFund', label: 'Provident Fund (PF)', type: 'number', placeholder: '0' })}
                  {field({ name: 'professionalTax', label: 'Professional Tax', type: 'number', placeholder: '0' })}
                </div>
                <div className="form-row">
                  {field({ name: 'tds', label: 'Tax Deducted at Source (TDS)', type: 'number', placeholder: '0' })}
                  {field({ name: 'loanDeduction', label: 'Loan Deduction', type: 'number', placeholder: '0' })}
                </div>
                <div className="form-row">
                  {field({ name: 'otherDeductions', label: 'Other Deductions', type: 'number', placeholder: '0' })}
                  <div className="form-group total-display">
                    <label>Total Deductions</label>
                    <div className="total-value">₹{calculateTotalDeductions().toLocaleString('en-IN')}</div>
                  </div>
                </div>
              </div>

              <div className="form-section net-salary-section">
                <h3>Net Salary Payable</h3>
                <div className="net-salary-display">
                  <div className="net-amount">₹{calculateNetSalary().toLocaleString('en-IN')}</div>
                  <div className="net-words">
                    {calculateNetSalary() > 0 ? numberToWords(calculateNetSalary()) : 'Enter valid amounts'}
                  </div>
                </div>
              </div>

              <div className="form-actions">
                <button type="submit" className="generate-btn" disabled={isGenerating}>
                  {isGenerating ? (
                    <>
                      <span className="spinner"></span>
                      Generating...
                    </>
                  ) : 'Generate Pay Slip'}
                </button>
                <button
                  type="button"
                  className="reset-btn"
                  onClick={() => {
                    setPaySlipData(initialPaySlipData);
                    setGeneratedSlip(null);
                  }}
                >
                  Reset Form
                </button>
              </div>
            </form>
          </div>

          {generatedSlip && (
            <div className="pay-slip-preview-section">
              <div className="preview-header">
                <h2>Generated Pay Slip</h2>
                <div className="download-options">
                  <button onClick={downloadAsPDF} className="download-btn pdf">
                    <span className="icon">📄</span> PDF
                  </button>
                  <button onClick={downloadAsImage} className="download-btn image">
                    <span className="icon">🖼️</span> Image
                  </button>
                  <button onClick={printPaySlip} className="download-btn print">
                    <span className="icon">🖨️</span> Print
                  </button>
                </div>
              </div>

              {/* One slip, used for preview, PDF, image and print */}
              <div className="slip-wrapper" style={{ overflowX: 'auto' }}>
                {renderSlipDocument()}
              </div>

              <div className="preview-actions">
                <button onClick={() => setGeneratedSlip(null)} className="new-slip-btn">
                  Generate New Slip
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PaySlip;